// components/attendance/components/StrangerDetailsModal.js
import PPEStatusBadge from 'components/ppeStatusBadge';
import { useEffect, useState } from 'react';
import Icon from '../../../components/appIcon';
import Image from '../../../components/appImage';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import Select from '../../../components/ui/select';
import axiosInstance from '../../../utils/axiosInstance';

const StrangerDetailsModal = ({
  stranger,
  isOpen,
  onClose,
  onIdentify,
  onMarkAsVisitor,
  onDismiss,
  onActivate,
  onAddNotes
}) => {
  const [notes, setNotes] = useState(stranger?.notes || '');
  const [isAddingNotes, setIsAddingNotes] = useState(false);
  const [showIdentifyForm, setShowIdentifyForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [identifyData, setIdentifyData] = useState({
    firstName: stranger?.first_name || '',
    lastName: stranger?.last_name || '',
    email: stranger?.email || '',
    employeeId: stranger?.id || '',
    department: stranger?.department || '',
    password: ''
  });
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    department: '',
    date_of_join: '',
    password: ''
  });

  // PPE related state
  const [ppeInfo, setPPEInfo] = useState({
    status: null,
    missingItems: [],
    detectedItems: []
  });
  const [loadingPPE, setLoadingPPE] = useState(false);

  // Create department options from fetched departments
  const departmentOptions = departments.map(dept => ({
    value: dept.id,
    label: dept.name
  }));

  // Fetch departments when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchDepartments = async () => {
        setLoadingDepartments(true);
        try {
          const response = await axiosInstance.get("/departments/getall");
          setDepartments(response.data || []);
        } catch (error) {
          console.error("Error fetching departments:", error);
          setDepartments([]);
        } finally {
          setLoadingDepartments(false);
        }
      };
      fetchDepartments();
    }
  }, [isOpen]);

  // Fetch attendance history and PPE information when modal opens
  useEffect(() => {
    if (isOpen && stranger) {
      const fetchAttendanceHistory = async () => {
        setLoadingHistory(true);
        setLoadingPPE(true);
        try {
          // Extract employee ID from stranger ID (format: STR-{id} or employee_id)
          const employeeId = stranger.id.startsWith('STR-')
            ? stranger.id.substring(4)
            : stranger.id;

          const response = await axiosInstance.get(`/attendance/getbyid/${employeeId}`);
          const records = response?.data?.data?.attendance || [];

          // Map records to expected format
          const history = records.map(record => ({
            id: record.id,
            timestamp: new Date(record.timestamp),
            location: record.location,
            cameraId: record.cameraId,
            status: record.status,
            ppeStatus: record.ppeStatus,
            missingPpeItems: typeof record.missingPpeItems === 'string' 
              ? JSON.parse(record.missingPpeItems) 
              : record.missingPpeItems || [],
            detectedPpeItems: typeof record.detectedPpeItems === 'string' 
              ? JSON.parse(record.detectedPpeItems) 
              : record.detectedPpeItems || []
          }));

          setAttendanceHistory(history);

          // Get PPE information from the most recent record
          if (history.length > 0) {
            const latestRecord = history[0]; // Assuming records are sorted by date
            setPPEInfo({
              status: latestRecord.ppeStatus,
              missingItems: latestRecord.missingPpeItems,
              detectedItems: latestRecord.detectedPpeItems
            });
          }
        } catch (error) {
          console.error("Error fetching attendance history:", error);
          setAttendanceHistory([]);
          setPPEInfo({
            status: null,
            missingItems: [],
            detectedItems: []
          });
        } finally {
          setLoadingHistory(false);
          setLoadingPPE(false);
        }
      };
      fetchAttendanceHistory();
    }
  }, [isOpen, stranger]);

  if (!isOpen || !stranger) return null;

  const handleAddNotes = async () => {
    setIsAddingNotes(true);
    try {
      await onAddNotes(stranger?.id, notes);
    } finally {
      setIsAddingNotes(false);
    }
  };

  // Validation function
  const validateForm = () => {
    const errors = {
      firstName: '',
      lastName: '',
      email: '',
      department: '',
      date_of_join: '',
      password: ''
    };
    
    let isValid = true;

    // First Name validation
    if (!identifyData.firstName.trim()) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    // Last Name validation
    if (!identifyData.lastName.trim()) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    // Email validation
    if (!identifyData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifyData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Department validation
    if (!identifyData.department) {
      errors.department = 'Department is required';
      isValid = false;
    }

    // Date of Joining validation
    if (!identifyData.date_of_join) {
      errors.date_of_join = 'Date of joining is required';
      isValid = false;
    }

    // Password validation
    if (!identifyData.password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (identifyData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleIdentify = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const employeeId = stranger.id.startsWith('STR-')
        ? stranger.id.substring(4)
        : stranger.id;

      const dataObj = {
        first_name: identifyData.firstName,
        last_name: identifyData.lastName,
        email: identifyData.email,
        department: identifyData.department,
        date_of_join: identifyData?.date_of_join,
        password: identifyData.password || 'defaultPassword123',
        isUnkown: 0
      };

      const formData = new FormData();
      formData.append('data', JSON.stringify(dataObj));

      const response = await axiosInstance.put(
        `/admin/update-profile/${stranger.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      await onIdentify(true);
      setShowIdentifyForm(false);
      onClose();
    } catch (error) {
      console.error("Error identifying stranger as employee:", error);
      alert("Failed to identify stranger as employee. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-success';
    if (confidence >= 70) return 'text-warning';
    return 'text-error';
  };

  // Handle image URL
  const BACKEND_URL = import.meta.env.VITE_API_URL;
  const imageUrl = stranger?.originalData?.images[0]?.imagePath
    
    ? `${BACKEND_URL}${stranger?.originalData?.images[0]?.imagePath}`
    : stranger.imageUrl;

  return (
    <div>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Eye" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Stranger Detection Details
              </h2>
              <p className="text-sm text-muted-foreground">
                Detection ID: {stranger?.id}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-80px)]">
          {/* Image Section */}
          <div className="lg:w-1/2 p-6 border-r border-border">
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative">
                <Image
                  src={imageUrl}
                  alt="Detected stranger"
                  className="w-full h-96 object-fit rounded-lg"
                />
                <div className="absolute top-3 right-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getConfidenceColor(stranger?.confidence)} bg-card`}>
                    {stranger?.confidence}% confidence
                  </span>
                </div>
              </div>

              {/* PPE Status Section */}
              {/* <div>
                <h4 className="font-medium text-foreground mb-3">PPE Compliance Status</h4>
                <div className="p-4 bg-muted rounded-lg">
                  {loadingPPE ? (
                    <div className="text-center py-4 text-muted-foreground">
                      Loading PPE information...
                    </div>
                  ) : (
                    <>
                      <PPEStatusBadge 
                        status={ppeInfo.status} 
                        missingItems={ppeInfo.missingItems} 
                      />
                      
                      {ppeInfo.detectedItems && ppeInfo.detectedItems.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-foreground mb-2">Detected PPE Items:</p>
                          <PPEItemsList items={ppeInfo.detectedItems} type="detected" />
                        </div>
                      )}
                      
                      {ppeInfo.missingItems && ppeInfo.missingItems.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-foreground mb-2">Missing PPE Items:</p>
                          <PPEItemsList items={ppeInfo.missingItems} type="missing" />
                        </div>
                      )}

                      {ppeInfo.status && (
                        <div className="mt-3 p-3 rounded-lg">
                          {ppeInfo.status === 'compliant' ? (
                            <div className="flex items-center space-x-2 text-green-600">
                              <Icon name="CheckCircle" size={16} />
                              <span className="text-sm font-medium">All required PPE items detected</span>
                            </div>
                          ) : ppeInfo.status === 'non_compliant' ? (
                            <div className="flex items-center space-x-2 text-red-600">
                              <Icon name="AlertCircle" size={16} />
                              <span className="text-sm font-medium">
                                {ppeInfo.missingItems?.length || 0} required PPE item(s) missing
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 text-gray-600">
                              <Icon name="HelpCircle" size={16} />
                              <span className="text-sm font-medium">PPE status unknown</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div> */}
            </div>
          </div>

          {/* Details Section */}
          <div className="lg:w-1/2 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="font-semibold text-lg text-foreground mb-4">Detection Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="MapPin" size={16} className="text-muted-foreground" />
                    <div>
                      <span className="text-sm text-muted-foreground">Location:</span>
                      <span className="ml-2 text-foreground font-medium">{stranger?.location}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Camera" size={16} className="text-muted-foreground" />
                    <div>
                      <span className="text-sm text-muted-foreground">Camera:</span>
                      <span className="ml-2 text-foreground font-medium">{stranger?.cameraName}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Clock" size={16} className="text-muted-foreground" />
                    <div>
                      <span className="text-sm text-muted-foreground">First Detected:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {stranger.detectedAt instanceof Date
                          ? stranger.detectedAt.toLocaleString()
                          : new Date(stranger.detectedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Eye" size={16} className="text-muted-foreground" />
                    <div>
                      <span className="text-sm text-muted-foreground">Total Detections:</span>
                      <span className="ml-2 text-foreground font-medium">{stranger?.detectionCount}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="Activity" size={16} className="text-muted-foreground" />
                    <div>
                      <span className="text-sm text-muted-foreground">Status:</span>
                      <span className="ml-2 text-foreground font-medium">
                        {stranger?.status === 'new' ? 'New Detection' :
                         stranger?.status === 'active' ? 'Active' :
                         stranger?.status === 'visitor' ? 'Visitor' :
                         stranger?.status === 'inactive' ? 'Inactive' :
                         stranger?.status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detection History */}
              <div>
                <h4 className="font-medium text-foreground mb-3">Recent Detection History</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {loadingHistory ? (
                    <div className="text-center py-4 text-muted-foreground">Loading detection history...</div>
                  ) : attendanceHistory.length > 0 ? (
                    attendanceHistory.map((detection, index) => (
                      <div key={detection.id} className="flex items-center justify-between p-3 bg-muted rounded-lg mb-2">
                        <div className="flex items-center space-x-3">
                          <Icon name="MapPin" size={14} className="text-muted-foreground" />
                          <div>
                            <div className="text-sm font-medium text-foreground">{detection.location}</div>
                            <div className="text-xs text-muted-foreground">
                              {detection.timestamp instanceof Date
                                ? detection.timestamp.toLocaleString()
                                : new Date(detection.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-x-2">
                          <span className="text-xs px-2 py-1 rounded-full bg-secondary text-secondary-foreground">
                            {detection.status}
                          </span>
                          <span className="text-sm font-medium text-muted-foreground">
                            {detection.cameraId}
                          </span>
                          {/* PPE status for this detection */}
                          {detection.ppeStatus && (
                            <PPEStatusBadge 
                              status={detection.ppeStatus} 
                              missingItems={detection.missingPpeItems}
                              size="sm"
                            />
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">No detection history available</div>
                  )}
                </div>
              </div>

              {/* Notes Section */}
              <div>
                <h4 className="font-medium text-foreground mb-3">Notes</h4>
                <div className="space-y-3">
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e?.target?.value)}
                    placeholder="Add notes about this detection..."
                    className="w-full h-24 px-3 py-2 border border-border rounded-md bg-input text-foreground resize-none focus:ring-2 focus:ring-ring focus:border-transparent"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddNotes}
                    loading={isAddingNotes}
                    iconName="Save"
                    iconPosition="left"
                  >
                    Save Notes
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                <h4 className="font-medium text-foreground">Actions</h4>
                <div className="flex flex-col space-y-2">
                  <Button
                    variant="default"
                    onClick={() => setShowIdentifyForm(true)}
                    iconName="UserPlus"
                    iconPosition="left"
                    fullWidth
                  >
                    Identify as Employee
                  </Button>
                  {stranger?.status === 'inactive' ? (
                    <Button
                      variant="default"
                      onClick={() => onActivate(stranger.id)}
                      iconName="Check"
                      iconPosition="left"
                      fullWidth
                    >
                      Activate Detection
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => onDismiss(stranger.id)}
                      iconName="X"
                      iconPosition="left"
                      fullWidth
                    >
                      Dismiss Detection
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Identify Form Modal */}
        {showIdentifyForm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full">
              <h3 className="font-semibold text-lg text-foreground mb-4">
                Identify as Employee
              </h3>
              <div className="space-y-4">
                <Input
                  label="Employee ID"
                  value={identifyData?.employeeId}
                  onChange={(e) => setIdentifyData({ ...identifyData, employeeId: e?.target?.value })}
                  placeholder="EMP001"
                  disabled={true}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Input
                      label="First Name"
                      value={identifyData?.firstName}
                      onChange={(e) => setIdentifyData({ ...identifyData, firstName: e?.target?.value })}
                      placeholder="John"
                      className={formErrors.firstName ? "border-error" : ""}
                    />
                    {formErrors.firstName && (
                      <p className="text-xs text-error mt-1">{formErrors.firstName}</p>
                    )}
                  </div>
                  <div>
                    <Input
                      label="Last Name"
                      value={identifyData?.lastName}
                      onChange={(e) => setIdentifyData({ ...identifyData, lastName: e?.target?.value })}
                      placeholder="Doe"
                      className={formErrors.lastName ? "border-error" : ""}
                    />
                    {formErrors.lastName && (
                      <p className="text-xs text-error mt-1">{formErrors.lastName}</p>
                    )}
                  </div>
                </div>
                <div>
                  <Input
                    label="Email"
                    type="email"
                    value={identifyData?.email}
                    onChange={(e) => setIdentifyData({ ...identifyData, email: e?.target?.value })}
                    placeholder="john.doe@company.com"
                    className={formErrors.email ? "border-error" : ""}
                  />
                  {formErrors.email && (
                    <p className="text-xs text-error mt-1">{formErrors.email}</p>
                  )}
                </div>
                <div>
                  <Select
                    label="Department"
                    options={departmentOptions}
                    value={identifyData.department}
                    onChange={(value) => setIdentifyData({ ...identifyData, department: value })}
                    placeholder={loadingDepartments ? "Loading departments..." : "Select department"}
                    className={formErrors.department ? "border-error" : ""}
                  />
                  {formErrors.department && (
                    <p className="text-xs text-error mt-1">{formErrors.department}</p>
                  )}
                </div>
                <div>
                  <Input
                    label="Date of Joining"
                    type="date"
                    value={identifyData.date_of_join ? identifyData.date_of_join.split("T")[0] : ""}
                    onChange={(e) => setIdentifyData({ ...identifyData, date_of_join: e?.target?.value })}
                    className={formErrors.date_of_join ? "border-error" : ""}
                  />
                  {formErrors.date_of_join && (
                    <p className="text-xs text-error mt-1">{formErrors.date_of_join}</p>
                  )}
                </div>
                <div>
                  <Input
                    label="Password"
                    type="password"
                    value={identifyData?.password}
                    onChange={(e) => setIdentifyData({ ...identifyData, password: e?.target?.value })}
                    placeholder="Enter password"
                    className={formErrors.password ? "border-error" : ""}
                  />
                  {formErrors.password && (
                    <p className="text-xs text-error mt-1">{formErrors.password}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">Password must be at least 6 characters</p>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowIdentifyForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleIdentify}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Saving Employee...' : 'Save Employee'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrangerDetailsModal;